/**
 * Referências na instrução de um job (AB#86605, task 86871; AB#87442):
 * `@skill:<nome>`, `@mind:<field_name>`, `@workspace:<campo>` — os dados do
 * próprio workspace (nome, descrição, site, membros) — e `@tool:[<nome>]`, uma
 * Tool pelo nome da instalação, entre colchetes porque o nome tem espaços.
 * Mesma regra do api (`job_mentions.ts`) e do engine.
 */
export const MENTION_PATTERN = /@(?:(skill|mind|workspace):([A-Za-z0-9][A-Za-z0-9_-]*)|tool:\[([^\]\n]{1,120})\])/g;

export const MENTION_KINDS = ["skill", "mind", "workspace", "tool"] as const;

export type MentionKind = (typeof MENTION_KINDS)[number];

export interface Mention {
  kind: MentionKind;
  key: string;
}

/** Como a referência fica escrita no texto. */
export function mentionToken(mention: Mention): string {
  return mention.kind === "tool" ? `@tool:[${mention.key}]` : `@${mention.kind}:${mention.key}`;
}

/** A chave que identifica uma referência; o nome da Tool não diferencia maiúsculas, como no api. */
export function mentionId(mention: Mention): string {
  return `${mention.kind}:${mention.kind === "tool" ? mention.key.toLowerCase() : mention.key}`;
}

function mentionOfMatch(match: RegExpMatchArray): Mention {
  return match[3] !== undefined
    ? { kind: "tool", key: match[3].trim() }
    : { kind: match[1] as MentionKind, key: match[2] };
}

/** As referências da instrução, sem repetir e na ordem em que aparecem. */
export function mentionsOf(text: string): Mention[] {
  const seen = new Set<string>();
  const out: Mention[] = [];
  for (const match of text.matchAll(MENTION_PATTERN)) {
    const mention = mentionOfMatch(match);
    const id = mentionId(mention);
    if (!mention.key || seen.has(id)) continue;
    seen.add(id);
    out.push(mention);
  }
  return out;
}

/** O texto em pedaços: trechos soltos e as referências, na ordem, para a visão renderizada. */
export function splitMentions(text: string): Array<string | Mention> {
  const out: Array<string | Mention> = [];
  let last = 0;
  for (const match of text.matchAll(MENTION_PATTERN)) {
    const index = match.index ?? 0;
    if (index > last) out.push(text.slice(last, index));
    out.push(mentionOfMatch(match));
    last = index + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/**
 * O `@` que está sendo digitado na posição do cursor, ou null. Abre a lista só
 * quando o `@` começa uma palavra — `ana@acme.com` não é referência — e fecha
 * no primeiro espaço; dentro dos colchetes de uma Tool, só no `]`.
 */
export function activeMention(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const match = /(^|\s)@(tool:\[[^\]\n]*|[A-Za-z0-9:_-]*)$/.exec(before);
  if (!match) return null;
  return { start: caret - match[2].length - 1, query: match[2] };
}

/**
 * O que a lista mostra para o que foi digitado depois do `@` (AB#87442): nada
 * digitado, os tipos; `tipo:`, os itens daquele tipo; outro texto, a busca em
 * todos os itens.
 */
export function pickerStep(query: string): { step: "kinds" } | { step: "items"; kind: MentionKind | null } {
  if (!query) return { step: "kinds" };
  if (!query.includes(":")) return { step: "items", kind: null };
  const prefix = query.split(":", 1)[0].toLowerCase();
  return {
    step: "items",
    kind: MENTION_KINDS.find((k) => k === prefix) ?? null,
  };
}

/** Troca o `@consulta` pela referência escolhida, com um espaço depois. */
export function insertMention(
  text: string,
  start: number,
  caret: number,
  mention: Mention,
): { text: string; caret: number } {
  const token = `${mentionToken(mention)} `;
  // Dentro dos colchetes de uma Tool, o resto do nome e o `]` que já estiver ali
  // saem junto; fora deles, só o resto da palavra.
  const insideBrackets = text.slice(start, caret).includes("[") && /^[^\]\n]*\]/.test(text.slice(caret));
  const after = text.slice(caret).replace(insideBrackets ? /^[^\]\n]*\]/ : /^\S*/, "");
  return {
    text: text.slice(0, start) + token + after.replace(/^ /, ""),
    caret: start + token.length,
  };
}

/** Troca o `@consulta` pelo tipo escolhido (`@skill:`), sem espaço: a lista segue aberta nos itens dele. */
export function insertKind(
  text: string,
  start: number,
  caret: number,
  kind: MentionKind,
): { text: string; caret: number } {
  const token = kind === "tool" ? "@tool:[" : `@${kind}:`;
  const after = text.slice(caret).replace(/^\S*/, "");
  return {
    text: text.slice(0, start) + token + after,
    caret: start + token.length,
  };
}

/** Filtra as opções pelo que já foi digitado depois do `@`, com ou sem o tipo. */
export function matchesQuery(query: string, kind: MentionKind, key: string, title?: string | null): boolean {
  const [prefix, rest] = query.includes(":") ? query.split(":", 2) : ["", query];
  if (prefix && !kind.startsWith(prefix.toLowerCase())) return false;
  const term = (rest ?? "").replace(/^\[/, "").trim().toLowerCase();
  if (!term) return true;
  return key.toLowerCase().includes(term) || (title ?? "").toLowerCase().includes(term);
}
